import React, { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Button,
    CircularProgress,
    Link,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { IoExtensionPuzzleOutline } from "react-icons/io5";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { TEST_JIGSAW_CANVAS_STORY_ID } from "../jigsaw-test/testJigsawCanvasStoryId";
import SuccessCard from "../../pages/puzzles/common/components/SuccessCard";
import { useConfetti } from "../../context/ConfettiContext";
import Logger from "../../services/Logger";
import AnythingService from "./AnythingService";

export default function AnythingMain() {
    const navigate = useNavigate();
    const { triggerConfetti } = useConfetti();
    const [successCardOpen, setSuccessCardOpen] = useState(false);
    const [migrateLoading, setMigrateLoading] = useState(false);
    const [migrateError, setMigrateError] = useState(null);
    const [migrateSuccess, setMigrateSuccess] = useState(null);

    const handleNavigateToTest = (path) => {
        navigate(path);
    };

    const handleConfettiClick = () => {
        triggerConfetti(10 * 1000); // Trigger confetti for 10 seconds
    };

    const handleShowSuccessCard = () => {
        setSuccessCardOpen(true);
        triggerConfetti(10 * 1000);
    };

    const handleMigrateClassicWordSearch = async () => {
        setMigrateLoading(true);
        setMigrateError(null);
        setMigrateSuccess(null);
        try {
            const result = await AnythingService.migrateClassicWordSearchTopicWords();
            const message =
                result?.message
                || (result != null ? JSON.stringify(result, null, 2) : "Migration finished.");
            setMigrateSuccess(
                typeof message === "string" ? message : JSON.stringify(result, null, 2),
            );
            triggerConfetti(4 * 1000);
        } catch (err) {
            Logger.error("Classic word-search migrate error:", err);
            setMigrateError(err.message || "Migration failed.");
        } finally {
            setMigrateLoading(false);
        }
    };

    const buttons = [
    {
        label: "Send notification",
        path: "/admin/system/anything/send-notification",
        description: "Send a test web notification to this PC only (your signed-in account).",
    },
    {
        label: "Google AdSense",
        path: "/admin/system/anything/test-adsense",
        color: "secondary",
        description: "Open the AdSense test page and show the horizontal banner.",
    },
    {
        label: "Monetag",
        path: "/admin/system/anything/test-monetag",
        color: "secondary",
        description: "Open the Monetag test page for all four tags: vignette, in-page push, onclick, and push notifications.",
    },
    {
        label: "Test Genre",
        path: "/admin/system/anything/genre",
        description: "Test genre management and categorization features."
    },
    {
        label: "Test Grid",
        path: "/admin/system/anything/grid",
        description: "Test grid layout and puzzle grid functionality."
    },
    {
        label: "Get AWS Credentials",
        path: "/admin/system/anything/credentials",
        description: "Retrieve and manage AWS credentials and configuration."
    },
    {
        label: "File Upload",
        path: "/admin/system/anything/file-upload",
        description: "Test file upload functionality and file handling."
    },
    {
        label: "Send Email",
        path: "/admin/system/anything/email",
        description: "Test email sending functionality and email templates."
    },
    {
        label: "Grid Puzzle - Image Upload",
        path: "/admin/system/anything/grid",
        icon: <IoExtensionPuzzleOutline size="1.2em" />,
        description: "Upload and manage images for grid puzzle generation."
    },
    {
        label: "Generate preview image",
        path: "/admin/system/anything/gen-image-preview",
        description: "Generate preview images for puzzles (wordsearch, hiddenword, crossword)."
    },
    {
        label: "Stripe integration",
        path: "/admin/system/stripe",
        description: "Test Stripe webhook integration by sending test events to the server."
    },
    {
        label: "Translate",
        path: "/admin/system/anything/translate",
        description: "Translate words and phrases using translation service."
    },
    {
        label: "Hook the word",
        path: "/admin/system/anything/hook-it",
        description: "Generate attractive titles from content or word lists."
    },
    {
        label: "Create French Puzzles",
        path: "/admin/system/anything/create-french-puzzles",
        description: "Create French versions for all ready-for-review Grid and Crossword puzzles."
    },
    {
        label: "Place Words on Word Search",
        path: "/admin/system/anything/wordsearch-place-words",
        description: "Replace words on a word search puzzle by template ID."
    },
    {
        label: migrateLoading ? "Migrating word search 12×12…" : "Migrate word search 12×12",
        onClick: handleMigrateClassicWordSearch,
        description:
            "PUBLISHED classic 12×12 only (state unchanged): strip non-topic words, letter-fill, remake preview + PDFs, and delete all word-search play records.",
        color: "warning",
        disabled: migrateLoading,
        icon: migrateLoading ? <CircularProgress size={16} color="inherit" /> : undefined,
    },
    {
        label: "Play a word",
        path: "/admin/system/anything/dic-play-word",
        description: "Fetch dictionary word audio (MP3) and play it in the browser."
    },
    {
        label: "Batch all of the word of day",
        path: "/admin/system/anything/word-of-day-batch",
        description: "Backfill word of the day for published templates from a start date (test API)."
    },
    {
        label: "Test new prompt v2",
        path: "/admin/system/anything/test-prompt-v2",
        color: "secondary",
        description:
            "Regenerate a story illustration from its image prompt into story-revision/ and compare current vs updated.",
    },
    {
        label: "Test word-puzzle photo prompt",
        path: "/admin/system/anything/test-word-puzzle-photo",
        color: "secondary",
        description:
            "Compare current word-puzzle story art vs a standalone-like photo wrap (revision only; production gen-story unchanged).",
    },
    ];

    return (
        <>
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                    }}
                >
                    {buttons.map((button, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                            }}
                        >
                            <Button
                                variant="contained"
                                color={button.color || "primary"}
                                size="small"
                                startIcon={button.icon}
                                disabled={Boolean(button.disabled)}
                                onClick={button.onClick || (() => handleNavigateToTest(button.path))}
                                sx={{
                                    ...button.sx,
                                }}
                            >
                                {button.label}
                            </Button>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {button.description}
                            </Typography>
                        </Box>
                    ))}
                    {migrateError ? (
                        <Alert severity="error" onClose={() => setMigrateError(null)}>
                            {migrateError}
                        </Alert>
                    ) : null}
                    {migrateSuccess ? (
                        <Alert
                            severity="success"
                            onClose={() => setMigrateSuccess(null)}
                            sx={{ "& pre": { m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" } }}
                        >
                            {migrateSuccess.includes("\n") || migrateSuccess.startsWith("{") ? (
                                <pre>{migrateSuccess}</pre>
                            ) : (
                                migrateSuccess
                            )}
                        </Alert>
                    ) : null}
                </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }} sx={{ minWidth: 0 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="anything-system-content"
                            id="anything-system-header"
                        >
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                                System
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleNavigateToTest("/admin/system/anything/crypto")}
                                    sx={{ alignSelf: "flex-start" }}
                                >
                                    Crypto
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    Put plain text and encrypt it.
                                </Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="anything-ui-content"
                            id="anything-ui-header"
                        >
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                                UI
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                <Box>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        size="small"
                                        onClick={handleShowSuccessCard}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Show success card
                                    </Button>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                        Opens the completion overlay and runs confetti.
                                    </Typography>
                                </Box>
                                <Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleConfettiClick}
                                        sx={{
                                            alignSelf: "flex-start",
                                            backgroundColor: "#ff6b6b",
                                            "&:hover": {
                                                backgroundColor: "#ff5252",
                                            },
                                        }}
                                    >
                                        🎉 Confetti
                                    </Button>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                        Trigger confetti only (no card).
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded disableGutters>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="anything-jigsaw-puzzle-content"
                            id="anything-jigsaw-puzzle-header"
                        >
                            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Jigsaw Puzzle
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleNavigateToTest("/admin/system/anything/svg-to-json")}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Convert SVG to JSON
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        Parse an SVG die-line file and output piece paths as JSON.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleNavigateToTest("/admin/system/anything/svg-overlay")}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Convert overlay SVG
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        Convert a die-line SVG to a white-stroke overlay (no border, no dimensions).
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleNavigateToTest("/admin/system/anything/test-jigsaw")}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Test Jigsaw Puzzle
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        Load a story image and overlay the 8×8 jigsaw SVG template.
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="secondary"
                                        onClick={() => handleNavigateToTest("/admin/system/anything/test-jigsaw-canvas")}
                                        sx={{ alignSelf: "flex-start" }}
                                    >
                                        Test canvas
                                    </Button>
                                    <Typography variant="body2" color="text.secondary">
                                        Two-lane canvas (grid tray + scattered pool), same story as public play{" "}
                                        <Link component={RouterLink} to={`/puzzle/play-jigsaw/${TEST_JIGSAW_CANVAS_STORY_ID}`}>
                                            /puzzle/play-jigsaw/{TEST_JIGSAW_CANVAS_STORY_ID}
                                        </Link>
                                        . Uses copied components under{" "}
                                        <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                                            src/admin/jigsaw-test/
                                        </Typography>{" "}
                                        (<Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                                            Test*
                                        </Typography>
                                        ) for safe experiments.
                                    </Typography>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </Grid>
            </Grid>

            <SuccessCard
                variant="modal"
                open={successCardOpen}
                onClose={() => setSuccessCardOpen(false)}
                stats={{ time: "12:34", accuracy: 98 }}
                onNext={() => {
                    setSuccessCardOpen(false);
                    navigate("/puzzles");
                }}
            />
        </>
    );
}
